import {
  CASE_TYPE_SCHEMA,
  enumValuesFromSchema,
  getSchemaAt,
} from "../../../case-spec";
import { getAtPath, setAtPath } from "./path-utils";

/** Schema enum options at a case JSON path. Empty when the path is not an enum. */
export function enumOptionsAt(path: string): string[] {
  return enumValuesFromSchema(getSchemaAt(CASE_TYPE_SCHEMA, path));
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

export function asRecord(value: unknown): Record<string, unknown> {
  return isRecord(value) ? value : {};
}

export function str(value: unknown, fallback = ""): string {
  return typeof value === "string" ? value : fallback;
}

export function bool(value: unknown, fallback = false): boolean {
  return typeof value === "boolean" ? value : fallback;
}

export function num(value: unknown, fallback = 0): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

/** Names from canon.people for pickers. */
export function peopleNames(root: unknown): string[] {
  const people = asArray(getAtPath(root, "canon.people"));
  const names: string[] = [];
  for (const p of people) {
    if (!isRecord(p)) continue;
    const name = str(p.name).trim();
    if (name) names.push(name);
  }
  return names;
}

export type CastRole = "protagonist" | "guide" | "player";

const ROLE_FLAG: Record<CastRole, string> = {
  protagonist: "is_protagonist",
  guide: "is_guide",
  player: "is_player",
};

/**
 * Set game.cast[role] and dual-write is_* flags on canon.people.
 * Exactly one person should hold each role flag after this call.
 */
export function setCastRole(
  root: unknown,
  role: CastRole,
  name: string,
): unknown {
  const trimmed = name.trim();
  let next = setAtPath(root, `game.cast.${role}`, trimmed);
  const people = asArray(getAtPath(next, "canon.people"));
  if (people.length === 0) return next;
  const flag = ROLE_FLAG[role];
  const updated = people.map((p) => {
    if (!isRecord(p)) return p;
    return {
      ...p,
      [flag]: str(p.name).trim() === trimmed && trimmed.length > 0,
    };
  });
  return setAtPath(next, "canon.people", updated);
}

/** Toggle/set a person role flag and mirror into game.cast. */
export function setPersonRole(
  root: unknown,
  personIndex: number,
  role: CastRole,
  enabled: boolean,
): unknown {
  const people = asArray(getAtPath(root, "canon.people"));
  const person = people[personIndex];
  if (!isRecord(person)) return root;
  const name = str(person.name).trim();
  const flag = ROLE_FLAG[role];

  if (enabled) {
    const updated = people.map((p, i) => {
      if (!isRecord(p)) return p;
      return { ...p, [flag]: i === personIndex };
    });
    let next = setAtPath(root, "canon.people", updated);
    if (name) next = setAtPath(next, `game.cast.${role}`, name);
    return next;
  }

  const updated = people.map((p, i) => {
    if (!isRecord(p) || i !== personIndex) return p;
    return { ...p, [flag]: false };
  });
  let next = setAtPath(root, "canon.people", updated);
  const castName = str(getAtPath(next, `game.cast.${role}`)).trim();
  if (castName && castName === name) {
    next = setAtPath(next, `game.cast.${role}`, "");
  }
  return next;
}

/** When a person renames, keep cast in sync if they held a role. */
export function renamePersonKeepCast(
  root: unknown,
  personIndex: number,
  newName: string,
): unknown {
  const people = asArray(getAtPath(root, "canon.people"));
  const person = people[personIndex];
  if (!isRecord(person)) return root;
  const oldName = str(person.name).trim();
  const trimmed = newName.trim();
  const updated = people.map((p, i) => {
    if (i !== personIndex || !isRecord(p)) return p;
    return { ...p, name: trimmed };
  });
  let next = setAtPath(root, "canon.people", updated);
  for (const role of ["protagonist", "guide", "player"] as const) {
    if (str(getAtPath(next, `game.cast.${role}`)).trim() === oldName) {
      next = setAtPath(next, `game.cast.${role}`, trimmed);
    }
  }
  return next;
}

export function threadKey(sender: string, receiver: string): string {
  return [sender.trim(), receiver.trim()].filter(Boolean).sort().join(" ↔ ");
}

export type PanelProps = {
  root: unknown;
  disabled?: boolean;
  onChangeRoot: (next: unknown) => void;
};
